import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useTrainingCycles } from '../../hooks/coach/useTrainingCycles';
import { useAssignCycleDialog } from '../../hooks/dialogs/useAssignCycleDialog';
import { AssignCycleDialog } from '../dialogs/AssignCycleDialog';
import { TrainingCycleTemplateDialog } from '../dialogs/TrainingCycleTemplateDialog';
import { useTrainingCycleTemplateDialog } from '../../hooks/dialogs/useTrainingCycleTemplateDialog';
import { useUser } from '../../contexts/UserContext';
import { api } from '../../services/api-client';
import { CycleCard, ICycleTemplate } from './CycleCard';
import ImportWorkoutCycleAIDialog from '../dialogs/ImportWorkoutCycleAIDialog';

export function TrainingCyclesTab() {
  const intl = useIntl();
  const { coach, user } = useUser();
  const { trainingCycleTemplates, handleDeleteCycleTemplate, setRefreshKey } = useTrainingCycles();
  const [workouts, setWorkouts] = useState<any[]>([]);
  const [deletedWorkoutTemplates, setDeletedWorkoutTemplates] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [importCycleVisible, setImportCycleVisible] = useState(false);

  useEffect(() => {
    const fetchWorkouts = async () => {
      try {
        const { data } = await api.workout.findAllWorkoutTemplatesByCoachId();
        setWorkouts(data ?? []);
      } catch (error) {
        console.error('Error fetching workouts:', error);
      }
    };
    fetchWorkouts();
  }, [coach?.id]);

  useEffect(() => {
    const fetchDeletedWorkoutTemplates = async () => {
      try {
        const { data } = await api.workout.fetchDeletedWorkoutTemplatesByCoachId();
        setDeletedWorkoutTemplates(data ?? []);
      } catch (error) {
        console.error('Error fetching deleted workout templates:', error);
      }
    };
    fetchDeletedWorkoutTemplates();
  }, [coach?.id]);

  useEffect(() => {
    const loadStudents = async () => {
      try {
        const { data } = await api.coach.fetchStudents();
        setStudents(data ?? []);
      } catch (error) {
        console.error('Error loading students:', error);
      }
    };
    loadStudents();
  }, [user?.userId]);

  const assignCycleDialog = useAssignCycleDialog(() => {
    setRefreshKey((prev) => prev + 1);
  });

  const cycleTemplateDialog = useTrainingCycleTemplateDialog(workouts, deletedWorkoutTemplates, () => {
    setRefreshKey((prev) => prev + 1);
  });

  const filtered = useMemo<ICycleTemplate[]>(() => {
    const term = search.trim().toLowerCase();
    const list: ICycleTemplate[] = trainingCycleTemplates ?? [];
    if (!term) return list;
    return list.filter((t) => t.name?.toLowerCase().includes(term));
  }, [trainingCycleTemplates, search]);

  const stats = useMemo(() => {
    const list: ICycleTemplate[] = trainingCycleTemplates ?? [];
    const totalCycles = list.length;
    const totalWeeks = list.reduce((sum, t) => sum + (t.trainingWeeks?.length ?? 0), 0);
    const totalSessions = list.reduce(
      (sum, t) => sum + (t.trainingWeeks ?? []).reduce((wsum, w) => wsum + (w.trainingSessions?.length ?? 0), 0),
      0
    );
    return { totalCycles, totalWeeks, totalSessions };
  }, [trainingCycleTemplates]);

  return (
    <div style={{ paddingBottom: '0.5rem' }}>
      {/* Header */}
      <div className="flex flex-column gap-1 mb-3">
        <h2
          style={{
            margin: 0,
            fontSize: '1.4rem',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            color: 'var(--ios-text)'
          }}
        >
          <FormattedMessage id="coach.sections.trainingCycles" />
        </h2>
        <small style={{ color: 'var(--ios-text-secondary)', fontSize: '0.82rem' }}>
          <FormattedMessage id="coach.trainingCycle.description" />
        </small>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-2 mb-3">
        <StatPill
          label={intl.formatMessage({ id: 'plansPage.stats.cyclesTotal' })}
          value={stats.totalCycles}
          color="#4d7c0f"
        />
        <StatPill
          label={intl.formatMessage({ id: 'plansPage.stats.cyclesWeeks' })}
          value={stats.totalWeeks}
          color="#22c55e"
        />
        <StatPill
          label={intl.formatMessage({ id: 'plansPage.stats.cyclesSessions' })}
          value={stats.totalSessions}
          color="#f59e0b"
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 align-items-center mb-3">
        <span className="p-input-icon-left" style={{ flex: '1 1 220px', minWidth: 0 }}>
          <i className="pi pi-search" />
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={intl.formatMessage({ id: 'plansPage.cycles.search.placeholder' })}
            className="w-full"
          />
        </span>
        <Button
          label={intl.formatMessage({ id: 'coach.importCycleAI.entryButton' })}
          icon="pi pi-sparkles"
          className="p-button-sm"
          onClick={() => setImportCycleVisible(true)}
          tooltip="Importar una planificación entera desde foto, Excel o texto"
          style={{
            background: 'rgba(99, 102, 241, 0.18)',
            color: '#4f46e5',
            border: 'none',
            fontWeight: 600
          }}
        />
        <Button
          label={intl.formatMessage({ id: 'plansPage.createCycle' })}
          icon="pi pi-plus"
          className="p-button-sm"
          onClick={cycleTemplateDialog.handleOpenNewTemplate}
        />
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <EmptyState
          icon="pi pi-calendar-plus"
          title={intl.formatMessage({
            id: search ? 'plansPage.empty.search.title' : 'plansPage.empty.cycles.title'
          })}
          description={intl.formatMessage({
            id: search ? 'plansPage.empty.search.description' : 'plansPage.empty.cycles.description'
          })}
        />
      ) : (
        <div className="grid">
          {filtered.map((template) => (
            <div key={template.id} className="col-12 sm:col-6 lg:col-4">
              <CycleCard
                template={template}
                onAssign={() => assignCycleDialog.handleOpenDialog(template)}
                onView={() => cycleTemplateDialog.handleViewCycle(template.id)}
                onEdit={() => cycleTemplateDialog.handleEditCycle(template.id)}
                onDelete={() => handleDeleteCycleTemplate(template.id)}
              />
            </div>
          ))}
        </div>
      )}

      <AssignCycleDialog
        visible={assignCycleDialog.isAssignCycleDialogVisible}
        onHide={assignCycleDialog.handleCloseDialog}
        selectedCycle={assignCycleDialog.selectedCycle}
        selectedClient={assignCycleDialog.selectedClient}
        setSelectedClient={assignCycleDialog.setSelectedClient}
        startDate={assignCycleDialog.startDate}
        setStartDate={assignCycleDialog.setStartDate}
        endDate={assignCycleDialog.endDate}
        setEndDate={assignCycleDialog.setEndDate}
        onAssign={assignCycleDialog.handleAssignCycleToClient}
        students={students}
      />
      <TrainingCycleTemplateDialog
        visible={cycleTemplateDialog.isTemplateDialogVisible}
        onHide={cycleTemplateDialog.handleCloseDialog}
        templateName={cycleTemplateDialog.templateName}
        setTemplateName={cycleTemplateDialog.setTemplateName}
        templateDuration={cycleTemplateDialog.templateDuration}
        selectedDay={cycleTemplateDialog.selectedDay}
        setSelectedDay={cycleTemplateDialog.setSelectedDay}
        selectedWorkout={cycleTemplateDialog.selectedWorkout}
        setSelectedWorkout={cycleTemplateDialog.setSelectedWorkout}
        isDurationInWeeks={cycleTemplateDialog.isDurationInWeeks}
        applyToAllWeeks={cycleTemplateDialog.applyToAllWeeks}
        setApplyToAllWeeks={cycleTemplateDialog.setApplyToAllWeeks}
        isReadOnly={cycleTemplateDialog.isReadOnly}
        setIsReadOnly={cycleTemplateDialog.setIsReadOnly}
        selectedCycleId={cycleTemplateDialog.selectedCycleId}
        templateWeeks={cycleTemplateDialog.templateWeeks}
        handleDurationTypeChange={cycleTemplateDialog.handleDurationTypeChange}
        handleRemoveWorkoutFromWeek={cycleTemplateDialog.handleRemoveWorkoutFromWeek}
        handleAddWorkout={cycleTemplateDialog.handleAddWorkout}
        handleCreateTemplate={cycleTemplateDialog.handleCreateTemplate}
        handleDurationChange={cycleTemplateDialog.handleDurationChange}
        workouts={cycleTemplateDialog.workouts}
        deletedWorkoutTemplates={cycleTemplateDialog.deletedWorkoutTemplates}
      />
      <ImportWorkoutCycleAIDialog
        visible={importCycleVisible}
        onHide={() => setImportCycleVisible(false)}
        onCreated={() => {
          // Refrescar la lista de ciclos para que el nuevo aparezca.
          setRefreshKey((k: number) => k + 1);
        }}
      />
    </div>
  );
}

function StatPill({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.45rem 0.85rem',
        background: 'var(--ios-card-bg)',
        border: '1px solid var(--ios-card-border)',
        borderRadius: 'var(--ios-radius-pill)',
        boxShadow: 'var(--ios-shadow-sm)'
      }}
    >
      <span
        style={{
          width: '0.5rem',
          height: '0.5rem',
          borderRadius: '50%',
          background: color
        }}
      />
      <span style={{ fontSize: '0.75rem', color: 'var(--ios-text-secondary)', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: '0.95rem', fontWeight: 800, color: 'var(--ios-text)' }}>{value}</span>
    </div>
  );
}

function EmptyState({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div
      style={{
        background: 'var(--ios-card-bg)',
        border: '1px dashed var(--ios-card-border)',
        borderRadius: 'var(--ios-radius-lg)',
        padding: '2.5rem 1.5rem',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.5rem'
      }}
    >
      <i className={icon} style={{ fontSize: '2rem', color: 'var(--ios-text-tertiary)', marginBottom: '0.4rem' }} />
      <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--ios-text)' }}>{title}</h4>
      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--ios-text-secondary)' }}>{description}</p>
    </div>
  );
}
